import { useEffect, useRef, useState } from "react";
import {
  getCompaniesApi,
  createCompanyApi,
} from "../../services/company/companyService";
import toast from "react-hot-toast";

const CompanySelector = ({ value = [], onChange }: any) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const ref = useRef<HTMLDivElement>(null);

  const [showModal, setShowModal] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: "" });

  // ================= DEFAULT LOGIC =================
  const applyDefaultLogic = (list: any[]) => {
    if (list.length === 0) return [];

    const hasDefault = list.some((c) => c.isDefault);

    if (hasDefault) return list;

    //  assign first as default
    return list.map((c, index) => ({
      ...c,
      isDefault: index === 0,
    }));
  };

  // ================= LOAD =================
  const loadCompanies = async (reset = false) => {
    try {
      const res = await getCompaniesApi(search, reset ? 1 : page, 10);

      const data = res?.data || [];
      const total = res?.totalCount || 0;

      setCompanies((prev) => {
        const merged = reset ? data : [...prev, ...data];

        // remove duplicates
        return Array.from(
          new Map(merged.map((i: any) => [i.id, i])).values()
        );
      });

      const count = reset ? data.length : companies.length + data.length;
      setHasMore(count < total);
    } catch {
      toast.error("Failed to load companies");
    }
  };

  // ================= SEARCH =================
  useEffect(() => {
    setPage(1);
    loadCompanies(true);
  }, [search]);

  // ================= PAGINATION =================
  useEffect(() => {
    if (page > 1) loadCompanies();
  }, [page]);

  // ================= SCROLL =================
  const handleScroll = () => {
    const el = ref.current;
    if (!el || !hasMore) return;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      setPage((p) => p + 1);
    }
  };

  // ================= SELECT =================
  const toggleCompany = (c: any) => {
    const exists = value.find((v: any) => v.tenantId === c.id);

    let updated;

    if (exists) {
      //  remove
      updated = value.filter((v: any) => v.tenantId !== c.id);
    } else {
      //  add
      updated = [
        ...value,
        {
          tenantId: c.id,
          isDefault: value.length === 0,
        },
      ];
    }

    onChange(applyDefaultLogic(updated));
  };

  // ================= SET DEFAULT =================
  const setDefault = (id: string) => {
    const updated = value.map((v: any) => ({
      ...v,
      isDefault: v.tenantId === id,
    }));

    onChange(updated);
  };

  // ================= CREATE =================
  const handleCreateCompany = async () => {
    try {
      const res = await createCompanyApi(newCompany);

      toast.success("Company created");

      const newItem = {
        tenantId: res.id,
        isDefault: value.length === 0,
      };

      const updated = [...value, newItem];

      onChange(applyDefaultLogic(updated));

      setShowModal(false);
      setNewCompany({ name: "" });

      loadCompanies(true);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div className="border rounded-xl p-4 space-y-3">

      {/* SEARCH */}
      <input
        placeholder="Search company..."
        className="w-full border px-3 py-2 rounded"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* LIST */}
      <div
        ref={ref}
        onScroll={handleScroll}
        className="max-h-52 overflow-y-auto space-y-2 border rounded p-2"
      >
        {companies.map((c) => {
          const selected = value.find((v: any) => v.tenantId === c.id);

          return (
            <div
              key={c.id}
              className="flex items-center justify-between border rounded p-2"
            >
              <div className="flex items-center gap-2">

                {/* CHECKBOX */}
                <input
                  type="checkbox"
                  checked={!!selected}
                  onChange={() => {
                    // ❗ prevent removing last default
                    if (
                      selected?.isDefault &&
                      value.length === 1
                    ) {
                      toast.error("At least one default required");
                      return;
                    }

                    toggleCompany(c);
                  }}
                />

                <span>{c.name}</span>
              </div>

              {/* DEFAULT BUTTON */}
              {selected && (
                <button
                  type="button"
                  onClick={() => setDefault(c.id)}
                  className={`text-xs px-2 py-1 rounded ${
                    selected.isDefault
                      ? "bg-green-600 text-white"
                      : "bg-gray-200"
                  }`}
                >
                  Default
                </button>
              )}
            </div>
          );
        })}

        {!hasMore && (
          <div className="text-center text-xs text-gray-400 py-2">
            No more companies
          </div>
        )}
      </div>

      {/* CREATE COMPANY */}
      {/* <button
        type="button"
        onClick={() => setShowModal(true)}
        className="text-blue-600 text-sm"
      >
        + Create Company
      </button> */}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg w-80 space-y-3">

            <h3 className="font-semibold">Create Company</h3>

            <input
              placeholder="Company Name"
              className="w-full border px-3 py-2 rounded"
              value={newCompany.name}
              onChange={(e) =>
                setNewCompany({ name: e.target.value })
              }
            />

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowModal(false)}>
                Cancel
              </button>

              <button
                onClick={handleCreateCompany}
                className="bg-blue-600 text-white px-3 py-1 rounded"
              >
                Save
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default CompanySelector;